/**
 * Machine page › Cost — the source-drawer notes for the two headline tiles.
 *
 * The demo wrapped "All-in cost · 18 months" and "Cost per operating hour" in
 * `FIG(…, {calc})` (line 2220): a click opened the ledger or the per-machine
 * roll-up with a "How this figure is calculated" box above the rows. These
 * functions build those boxes from the same figures the tiles show.
 *
 * Transformation layer — pure, no React.
 *
 * @see src/versions/v1/modules/fleet/MachineCostTab.tsx
 * @see docs/logic/fleet/machine-page.md
 */

import type { SourceCalc } from '../../chrome-oem/SourceDrawerBody';
import { COST_PER_MACHINE } from '../../data/core';
import { fmt$, fmtk, groupNum } from '../../shared/oem-engine';
import { CALC_NOTE } from '../../shared/source-calc';
import type { MachineCostView } from './MachineScene.transform';

/** Demo line 2220. The All-in tile: every ledger line, summed by category. */
export function allInCalc(view: MachineCostView): SourceCalc {
  const lines = view.categories.filter((c) => c.amount > 0);
  return {
    formula: CALC_NOTE.machineAllIn,
    steps: [
      lines.map((c) => `${c.category} ${fmt$(c.amount)}`).join(' + '),
      `= ${fmt$(view.total)} (${fmtk(view.total)})`,
    ],
  };
}

/**
 * Demo line 2220. The cost-per-hour tile: the roll-up row's amount over its
 * operating hours. `null` when the machine has no roll-up row.
 */
export function costPerHourCalc(machineId: string): SourceCalc | null {
  const cp = COST_PER_MACHINE.find((c) => c.machine_id === machineId);
  if (!cp) return null;
  return {
    formula: 'Cost per operating hour = 18-month all-in cost ÷ operating hours over the same 18 months.',
    steps: [
      `cost_per_op_hour = ${fmt$(cp.amount_aud)} ÷ ${groupNum(cp.op_hours_18m)} operating hours = A$${cp.cost_per_op_hour_aud}/h`,
    ],
  };
}
